import React, { useEffect, useState } from "react";
import CircularLoading from "../../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { GoogleMapsProvider } from "../../contexts/GoogleMapsProvider"; // [GOOGLE_PROVIDER]
import { api } from "common";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../contexts/AlertContext";
import moment from "moment/min/moment-with-locales";
import { FirebaseConfig } from "../../config/FirebaseConfig";
// [GOOGLE_KEYS] Loader for dynamic Google Maps Key (V1.7.2 Unified)
// import { loadGooglePlaces } from "../../utils/googlePlacesLoader";
import { getLangFromPathname } from "../../utils/i18nRoute";

function AuthLoading(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showError } = useAlert();

  const {
    fetchUser,
    fetchCarTypes,
    fetchSettings,
    fetchBookings,
    fetchCancelReasons,
    fetchPromos,
    fetchNotifications,
    signOff,
    attachUserSessionWatcher,
    checkAccountRevokedStatus,
    fetchWithdraws,
    fetchPaymentMethods,
    fetchLanguages,
    fetchWalletHistory,
    fetchCars,
    fetchComplain,
    fetchSMTP,
    fetchSos,
    fetchSMSConfig,
    fetchFleetAdminEarnings,
    fetchZones,
    fetchCustomRoutes,
    updateProfile,
  } = api;
  const [profileRetryCount, setProfileRetryCount] = useState(0);

  // [ANTI_LOOP] Strict lock to ensure bootstrap actions only fire ONCE
  const bootstrapLock = React.useRef({
    settings: false,
    config: false,
    languages: false,
    profile: false,
    data: false
  });
  const bootstrappedUidRef = React.useRef(null);

  const auth = useSelector((state) => state.auth);
  const languagedata = useSelector((state) => state.languagedata);
  const settingsdata = useSelector((state) => state.settingsdata);

  // 1) Parallel bootstrap: fire all foundational queries concurrently on mount
  useEffect(() => {
    if (bootstrapLock.current.initial) return;
    bootstrapLock.current.initial = true;
    bootstrapLock.current.settings = true;
    bootstrapLock.current.config = true;

    dispatch(fetchSettings());
    dispatch(fetchLanguages());
    dispatch(fetchCarTypes());
    dispatch(fetchZones());
    dispatch(fetchCustomRoutes());
    dispatch(fetchUser());
  }, [dispatch, fetchSettings, fetchLanguages, fetchCarTypes, fetchZones, fetchCustomRoutes, fetchUser]);

  // Set document title dynamically once settings are loaded
  useEffect(() => {
    if (settingsdata.settings?.appName) {
      document.title = settingsdata.settings.appName;
    }
  }, [settingsdata.settings?.appName]);

  // 3) [I18N_FIX] Apply language strictly from URL once languages arrive
  useEffect(() => {
    try {
      if (!languagedata?.langlist) return;

      // URL is source of truth: /en/... => "en", else "fr"
      const urlLang = getLangFromPathname(window.location.pathname); // "en" or "fr"
      /* console.log(
        "[I18N_FIX] AuthLoading urlLang=",
        urlLang,
        "path=",
        window.location.pathname,
      ); */

      // Build bundles from Firebase languages list
      const bundlesByLocale = {};
      const list = Object.values(languagedata.langlist).filter(Boolean);

      list.forEach((l) => {
        if (l?.langLocale && l?.keyValuePairs) {
          bundlesByLocale[l.langLocale] = l.keyValuePairs;
        }
      });

      // Pick exact bundle for urlLang; support DB locales like en-US, fr-FR via prefix match
      let targetLocale = urlLang; // "en" or "fr"
      let targetBundle = bundlesByLocale[targetLocale];

      if (!targetBundle) {
        const prefixMatch = Object.keys(bundlesByLocale).find((k) =>
          String(k).toLowerCase().startsWith(urlLang),
        );
        if (prefixMatch) {
          targetLocale = prefixMatch; // e.g. "en-US"
          targetBundle = bundlesByLocale[prefixMatch];
        }
      }

      // Absolute fallback: first available bundle (dev safety)
      if (!targetBundle) {
        const anyKey = Object.keys(bundlesByLocale)[0];
        if (anyKey) {
          targetLocale = anyKey;
          targetBundle = bundlesByLocale[anyKey];
          console.warn(
            "[I18N_FIX] No bundle found for urlLang, fallback to",
            anyKey,
          );
        }
      }

      // Register ALL language bundles into i18n so switching language is instant without reload
      const ns = "translations";
      list.forEach((l) => {
        if (l?.langLocale && l?.keyValuePairs) {
          const loc = l.langLocale;
          const shortLoc = loc.split("-")[0].toLowerCase();
          i18n.addResourceBundle(loc, ns, l.keyValuePairs, true, true);
          if (shortLoc !== loc) {
            i18n.addResourceBundle(shortLoc, ns, l.keyValuePairs, true, true);
          }
        }
      });

      if (targetBundle) {
        // Force i18n language to URL lang (never localStorage, never Firebase default)
        // [FIX] Always trigger a refresh/load once bundle is injected to ensure UI sees new keys
        if (i18n.language !== urlLang) {
          i18n.changeLanguage(urlLang);
        } else {
          // If already on correct lang, force a reload of the namespace to trigger re-renders
          i18n.loadNamespaces(ns);
        }

        // Align moment with URL language (or map a dateLocale here if you want)
        moment.locale(urlLang);

        // Persist for other parts of app (but never read as authority)
        try {
          localStorage.setItem(
            "lang",
            JSON.stringify({ langLocale: urlLang, dateLocale: urlLang }),
          );
        } catch (e) {
          // ignore
        }

        /* console.log(
          "[I18N_FIX] AuthLoading applied language=",
          urlLang,
          "bundleFrom=",
          targetLocale,
          "i18n.language=",
          i18n.language,
        ); */
      } else {
        console.warn("[I18N_FIX] No bundle found at all. Using i18next fallback.");
      }

      bootstrapLock.current.languages = true;
    } catch (error) {
      console.error("[I18N_FIX] AuthLoading language bootstrap failed:", error);
      bootstrapLock.current.languages = true;
    }
  }, [languagedata?.langlist]);

  // 4) After auth.profile is ready -> load role-specific data
  useEffect(() => {
    if (auth.profile) {
      /* console.log(
        "[AUTH_GOOGLE] Profile detected in AuthLoading. usertype:",
        auth.profile.usertype,
        "is_raw:",
        !!auth.profile.is_raw,
      ); */

      // Wait for DB profile if redirect profile is raw
      if (auth.profile.is_raw) {
        // console.log("[AUTH_GOOGLE] Raw profile detected. Waiting for DB profile...");
        return;
      }

      if (auth.profile.usertype) {
        if (bootstrappedUidRef.current !== auth.profile.uid) {
          bootstrappedUidRef.current = auth.profile.uid;
          const role = auth.profile.usertype;

          // [FIX] Update 'lastLogin' (Last Active) at bootstrap to ensure activity is real-time
          if (auth.profile.uid) {
            dispatch(updateProfile({
              lastLogin: Date.now()
            })).catch((err) => {
              console.warn("[AuthLoading] lastLogin background update failed:", err.message);
            });
          }
          // console.log("[BOOTSTRAP] Profile ready. Loading essential data for role:", role);

          // ONLY load what is strictly necessary for initial UI mount.
          // Heavy root reads (fetchUsers, fetchBookings) should be handled by specific pages.
          if (role === "customer") {
            dispatch(fetchBookings());
            dispatch(fetchWalletHistory());
            dispatch(fetchPaymentMethods());
            dispatch(fetchCancelReasons());
          } else if (role === "driver") {
            dispatch(fetchBookings());
            dispatch(fetchWithdraws());
            dispatch(fetchPaymentMethods());
            dispatch(fetchCars());
            dispatch(fetchWalletHistory());
            dispatch(fetchCancelReasons());
          } else if (role === "admin") {
            dispatch(fetchBookings());
            dispatch(fetchPromos());
            dispatch(fetchNotifications());
            dispatch(fetchCancelReasons());
            dispatch(fetchWithdraws());
            dispatch(fetchComplain());
            dispatch(fetchPaymentMethods());
            dispatch(fetchCars());
            dispatch(fetchSMTP());
            dispatch(fetchSMSConfig());
            dispatch(fetchSos());
          } else if (role === "fleetadmin") {
            dispatch(fetchBookings());
            dispatch(fetchCars());
            dispatch(fetchCancelReasons());
            dispatch(fetchPaymentMethods());
            dispatch(fetchWalletHistory());
          } else {
            console.warn("[AUTH_GOOGLE] Invalid user type detected:", role);
            showError(t("not_valid_user_type"));
            dispatch(signOff());
          }
        }
        // If usertype exists and already bootstrapped, we are DONE.
      } else if (!auth.profile.usertype) {
        console.warn(`[AUTH_GOOGLE] uid=${auth.profile.uid} has profile but usertype=null. retryCount=${profileRetryCount}`);

        if (profileRetryCount < 3) {
          const timer = setTimeout(() => {
            // console.log("[AUTH_GOOGLE] Retrying fetchUser... attempt", profileRetryCount + 1);
            dispatch(fetchUser());
            setProfileRetryCount(prev => prev + 1);
          }, 2000);
          return () => clearTimeout(timer);
        } else {
          console.error("[AUTH_GOOGLE] Profile bootstrap timed out after 3 retries. Redirecting to /login.");
          window.location.replace("/login?profile_missing=1");
        }
      }
    } else {
      bootstrappedUidRef.current = null;
    }
  }, [
    auth.profile,
    dispatch,
    fetchBookings,
    fetchNotifications,
    fetchPromos,
    fetchWithdraws,
    signOff,
    fetchPaymentMethods,
    fetchWalletHistory,
    fetchCars,
    fetchComplain,
    fetchSMTP,
    fetchSMSConfig,
    fetchSos,
    fetchFleetAdminEarnings,
    t,
    showError,
    profileRetryCount,
    fetchCancelReasons,
    fetchUser,
    updateProfile,
  ]);

  // [CRITICAL] Immediate signoff if account deleted by admin
  useEffect(() => {
    const isDeleted = Boolean(
      auth.error?.msg === "ACCOUNT_DELETED" ||
      auth.accountDeleted ||
      auth.isDeleted ||
      (auth.profile && (auth.profile.accountDeleted === true || auth.profile.isDeleted === true))
    );
    if (isDeleted) {
      console.warn("[AuthLoading] Account deletion detected in auth state. Signing off immediately...");
      dispatch(signOff());
      showError(t("account_deleted_alert", "Votre compte a été supprimé."));
    }
  }, [
    auth.error?.msg,
    auth.accountDeleted,
    auth.isDeleted,
    auth.profile?.accountDeleted,
    auth.profile?.isDeleted,
    dispatch,
    signOff,
    showError,
    t,
  ]);

  // [CRITICAL] Real-time session watcher & security heartbeat for web-app
  useEffect(() => {
    const currentUid = auth.profile?.uid;
    if (!currentUid) return;

    let cleanupWatcher = null;
    if (typeof attachUserSessionWatcher === "function") {
      cleanupWatcher = attachUserSessionWatcher(currentUid, dispatch);
    }

    const checkStatus = async () => {
      try {
        if (typeof checkAccountRevokedStatus === "function") {
          const isRevoked = await checkAccountRevokedStatus(currentUid);
          if (isRevoked) {
            console.warn("[AuthLoading] Revoked status confirmed via heartbeat. Terminating session...");
            dispatch(signOff());
            showError(t("account_deleted_alert", "Votre compte a été supprimé."));
          }
        }
      } catch (_) { }
    };

    const intervalId = setInterval(checkStatus, 6000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkStatus();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (typeof cleanupWatcher === "function") cleanupWatcher();
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [auth.profile?.uid, attachUserSessionWatcher, checkAccountRevokedStatus, dispatch, signOff, showError, t]);

  // UI loading states
  if (settingsdata.loading && !settingsdata.settings) return <CircularLoading />;

  if (!settingsdata.settings) {
    if (settingsdata.error) {
      return (
        <div style={{ padding: 20, textAlign: "center" }}>
          <h3>{t("system_startup_failed")}</h3>
          <p>
            {settingsdata.error && settingsdata.error.msg
              ? settingsdata.error.msg
              : t("no_database_settings_found", "No Database Settings found")}
          </p>
          <small>{t("api_key_check")}: {FirebaseConfig.apiKey ? t("present") : t("missing")}</small>
        </div>
      );
    }
    return <CircularLoading />;
  }

  // Wait for auth to settle
  if (auth.loading) return <CircularLoading />;

  // Wait for profile bootstrap (bounded by profileRetryCount above)
  if (auth.profile && (auth.profile.is_raw || !auth.profile.usertype)) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularLoading />
        <p style={{ marginTop: 20, fontFamily: 'Roboto, sans-serif', color: '#666' }}>
          {t("profile_load_wait", "Chargement du profil...")}
        </p>
      </div>
    );
  }

  return (
    <GoogleMapsProvider>
      {props.children}
    </GoogleMapsProvider>
  );
}

export default AuthLoading;